/**
 * Report management API methods (create, list, download).
 */

import apiClient from './client';
import type { Report, ReportCreateRequest } from '@/types/report';

/** List reports for the current tenant. */
export async function getReports(): Promise<Report[]> {
  const { data } = await apiClient.get<Report[]>('/v1/reports');
  return data;
}

/** Create a new report (queues generation). */
export async function createReport(payload: ReportCreateRequest): Promise<Report> {
  const { data } = await apiClient.post<Report>('/v1/reports', payload);
  return data;
}

/** Get report metadata by ID. */
export async function getReport(reportId: string): Promise<Report> {
  const { data } = await apiClient.get<Report>(`/v1/reports/${reportId}`);
  return data;
}

/** Download a completed report PDF as a Blob. */
export async function downloadReport(reportId: string): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(`/v1/reports/${reportId}/download`, {
    responseType: 'blob',
  });
  return data;
}

/** Delete a report by ID. */
export async function deleteReport(reportId: string): Promise<void> {
  await apiClient.delete(`/v1/reports/${reportId}`);
}

/** Download all findings as a CSV file. Optionally filter by scan, severity, or status. */
export async function exportFindingsCsv(params?: {
  scan_id?: string;
  severity?: string;
  status?: string;
}): Promise<void> {
  const { data } = await apiClient.get<Blob>('/v1/reports/export/csv', {
    params,
    responseType: 'blob',
  });
  const url = URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url;
  a.download = `findings-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
